/**
 * PlayerSelector Component
 * 
 * Searchable list of available players for squad selection.
 * Includes non-selection reason inputs for players not in the squad.
 */
import { forwardRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search, Plus, MessageSquare } from 'lucide-react';

const DEFAULT_PRESET_REASONS = ['Injured', 'Suspended', 'Dropped', 'Holiday', 'Work', 'Unavailable'];

/**
 * @param {Object} props
 * @param {Object[]} props.availablePlayers - Filtered available players (not in lineup/subs)
 * @param {string} props.searchQuery - Current search query
 * @param {Function} props.onSearchChange - Handler for search query change
 * @param {Function} props.onPlayerClick - Handler for clicking an available player
 * @param {boolean} props.isViewOnly - Whether the selector is read-only
 * @param {number|null} props.selectedPosition - Currently selected formation position
 * @param {number|null} props.selectedBenchPosition - Currently selected bench position
 * @param {Object} props.nonSelectionReasons - Map of playerId to reason string
 * @param {Function} props.onReasonChange - Handler for reason change (playerId, reason)
 * @param {string[]} props.presetReasons - Custom preset reasons from settings
 */
export const PlayerSelector = forwardRef(function PlayerSelector({
  availablePlayers = [],
  searchQuery = '',
  onSearchChange,
  onPlayerClick,
  isViewOnly = false,
  selectedPosition = null,
  selectedBenchPosition = null,
  nonSelectionReasons = {},
  onReasonChange,
  presetReasons = DEFAULT_PRESET_REASONS
}, ref) {
  const hasSelection = selectedPosition !== null || selectedBenchPosition !== null;
  const [expandedReason, setExpandedReason] = useState(null);

  if (isViewOnly) {
    return null;
  }

  return (
    <Card className="rounded-2xl" ref={ref}>
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search players..."
              value={searchQuery}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="pl-9 rounded-xl"
              data-testid="player-search"
            />
          </div>

          {/* Available Players */}
          <div data-testid="available-players-section">
            <h4 className="text-sm font-medium text-gray-500 mb-2">
              Available Players ({availablePlayers.length})
            </h4>
            <div className="space-y-1">
              {availablePlayers.length > 0 ? (
                availablePlayers.map(player => {
                  const reason = nonSelectionReasons[player.id] || '';
                  const isExpanded = expandedReason === player.id;
                  const hasReason = !!reason;

                  return (
                    <div key={player.id}>
                      <div
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer group ${
                          hasSelection
                            ? 'hover:bg-emerald-50 hover:ring-2 hover:ring-emerald-300'
                            : 'hover:bg-gray-50'
                        }`}
                        data-testid={`available-player-${player.id}`}
                      >
                        <div 
                          className="flex items-center gap-2 flex-1"
                          onClick={() => onPlayerClick?.(player.id)}
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">
                            {player.jersey_number || '?'}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{player.name}</p>
                            <p className="text-xs text-gray-500">{player.position || 'No position'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Reason toggle button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${hasReason ? 'text-orange-500 opacity-100' : 'opacity-0 group-hover:opacity-100 text-gray-400'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedReason(isExpanded ? null : player.id);
                            }}
                            data-testid={`reason-toggle-${player.id}`}
                          >
                            <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="opacity-0 group-hover:opacity-100"
                            onClick={() => onPlayerClick?.(player.id)}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Reason input - expanded */}
                      {isExpanded && (
                        <div className="ml-10 mr-2 mb-2 mt-1 space-y-1.5">
                          <div className="flex flex-wrap gap-1">
                            {presetReasons.map((preset) => (
                              <button
                                key={preset}
                                type="button"
                                onClick={() => onReasonChange?.(player.id, reason === preset ? '' : preset)}
                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                  reason === preset
                                    ? 'bg-orange-500 text-white border-orange-500'
                                    : 'bg-white text-orange-600 border-orange-200 hover:bg-orange-50'
                                }`}
                                data-testid={`reason-preset-${player.id}-${preset.toLowerCase().replace(/\s/g, '-')}`}
                              >
                                {preset}
                              </button>
                            ))}
                          </div>
                          <Input
                            placeholder="Or type a custom reason..."
                            value={presetReasons.includes(reason) ? '' : reason}
                            onChange={(e) => onReasonChange?.(player.id, e.target.value)}
                            className="rounded-lg text-sm h-9 bg-orange-50 border-orange-200 focus:border-orange-400 placeholder:text-orange-300"
                            data-testid={`reason-input-${player.id}`}
                          />
                        </div>
                      )}
                      {/* Show reason badge when collapsed but has reason */}
                      {!isExpanded && hasReason && (
                        <div 
                          className="ml-10 mr-2 mb-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-md cursor-pointer hover:bg-orange-100 transition-colors"
                          onClick={() => setExpandedReason(player.id)}
                          data-testid={`reason-badge-${player.id}`}
                        >
                          {reason}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">
                  {searchQuery ? 'No players match your search' : 'All players assigned'}
                </p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export default PlayerSelector;
